/**
 * Company Form Component - Create/Edit companies
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { 
  SupabaseCompany,
  useCreateCompany,
  useUpdateCompany 
} from '@/hooks/useSupabaseCompanies';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const companyFormSchema = z.object({
  legacy_id: z.string().min(1, 'Legacy ID là bắt buộc'),
  company_name: z.string().min(1, 'Tên công ty là bắt buộc'),
  description: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
  website: z.string().url('Website không hợp lệ').optional().or(z.literal('')),
  logo_url: z.string().url('Logo URL không hợp lệ').optional().or(z.literal('')),
  is_active: z.boolean().default(true),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

interface CompanyFormProps {
  company?: SupabaseCompany;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onSuccess,
  onCancel
}) => {
  const { toast } = useToast();
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();

  const isEdit = !!company;
  
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      legacy_id: company?.legacy_id || '',
      company_name: company?.company_name || '',
      description: company?.description || '',
      address: company?.address || '',
      phone: company?.phone || '',
      email: company?.email || '',
      website: company?.website || '',
      logo_url: company?.logo_url || '',
      is_active: company?.is_active ?? true,
    },
  });

  const onSubmit = async (values: CompanyFormValues) => {
    try {
      if (isEdit && company) {
        await updateCompany.mutateAsync({
          id: company.id,
          updates: {
            ...values,
            sync_source: 'manual'
          }
        });
        
        toast({
          title: "Thành công",
          description: "Cập nhật công ty thành công",
        });
      } else {
        await createCompany.mutateAsync({
          ...values,
          sync_source: 'manual'
        } as any);
        
        toast({
          title: "Thành công", 
          description: "Tạo công ty mới thành công",
        });
      }
      
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Lỗi",
        description: error.message || `Lỗi khi ${isEdit ? 'cập nhật' : 'tạo'} công ty`,
        variant: "destructive",
      });
    }
  };

  const isLoading = createCompany.isPending || updateCompany.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="legacy_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Legacy ID *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="190EBD4E-FF811826"
                    {...field}
                    disabled={isEdit} // Don't allow editing legacy_id
                  />
                </FormControl>
                <FormDescription>
                  {isEdit ? 'Legacy ID không thể thay đổi' : 'ID từ hệ thống cũ (bắt buộc)'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tên công ty *</FormLabel>
                <FormControl>
                  <Input placeholder="EcoTech Company" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mô tả</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Mô tả về công ty..."
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Số điện thoại</FormLabel>
                <FormControl>
                  <Input placeholder="+84 123 456 789" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="contact@ecotech.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Địa chỉ</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Địa chỉ công ty..."
                  rows={2}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input placeholder="https://ecotech.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logo_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Logo URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://example.com/logo.png" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Trạng thái hoạt động</FormLabel>
                <FormDescription>
                  Công ty có đang hoạt động hay không
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              Hủy
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </div>
      </form>
    </Form>
  );
};